import {
	formNeedsWorkOrder,
	type BillingLogEntryResponse,
	type BillingOrderValues,
} from '../types/Contracts/BillingLog'
import type { FormDefinitionDetail, FormField } from '../types/Form/FormDefinition'
import type {
	FormSubmissionRequest,
	FormSubmissionValueRequest,
} from '../types/Form/FormSubmission'

export type DynamicFormSection = {
	sectionName: string
	fields: FormField[]
}

/** Only treat explicit true as locked — avoids React treating string "false" as truthy for `disabled`. */
export function fieldIsLocked(f: FormField): boolean {
	const v = f.isLocked as unknown
	return v === true || v === 1
}

export function sortFormFieldOptions(field: FormField) {
	return (field.options ?? []).slice().sort((a, b) => (a.value ?? '').localeCompare(b.value ?? ''))
}

/** Visible fields grouped by `sectionName`, fields sorted by `sortOrder` within each section. */
export function groupVisibleFormFieldsBySection(
	formDetail: FormDefinitionDetail,
): Map<string, FormField[]> {
	const visible = formDetail.fields.filter((f) => !f.isHidden)
	const bySection = new Map<string, FormField[]>()
	for (const f of visible) {
		const list = bySection.get(f.sectionName) ?? []
		list.push(f)
		bySection.set(f.sectionName, list)
	}
	for (const name of Array.from(bySection.keys())) {
		const list = bySection.get(name)
		if (list) list.sort((a, b) => a.sortOrder - b.sortOrder)
	}
	return bySection
}

/** Groups visible fields by `sectionName`, sorted by `sortOrder` within each section. */
export function buildDynamicFormSections(formDetail: FormDefinitionDetail): DynamicFormSection[] {
	const bySection = groupVisibleFormFieldsBySection(formDetail)
	return Array.from(bySection.entries()).map(([sectionName, fields]) => ({ sectionName, fields }))
}

/**
 * Like {@link buildDynamicFormSections}, but sections appear in `sectionDisplayOrder` first,
 * then any remaining sections in map iteration order.
 */
export function buildOrderedDynamicFormSections(
	formDetail: FormDefinitionDetail,
	sectionDisplayOrder: readonly string[],
): DynamicFormSection[] {
	const bySection = groupVisibleFormFieldsBySection(formDetail)
	const ordered: DynamicFormSection[] = []
	const used = new Set<string>()
	for (const name of sectionDisplayOrder) {
		const fields = bySection.get(name)
		if (fields?.length) {
			ordered.push({ sectionName: name, fields })
			used.add(name)
		}
	}
	Array.from(bySection.entries()).forEach(([name, fields]) => {
		if (!used.has(name) && fields.length) {
			ordered.push({ sectionName: name, fields })
		}
	})
	return ordered
}

/**
 * Pairs fields by sort order for a 6-field "dimensions" grid: (0|3), (1|4), (2|5).
 * Used by Work Scope Registry "Dimensions (Metric)" layout.
 */
export function buildFormFieldDimensionPairRows(fields: FormField[]): [FormField, FormField][] {
	const s = [...fields].sort((a, b) => a.sortOrder - b.sortOrder)
	if (s.length >= 6) {
		return [
			[s[0], s[3]],
			[s[1], s[4]],
			[s[2], s[5]],
		]
	}
	return []
}

export function chunkArray<T>(arr: T[], chunkSize: number): T[][] {
	const out: T[][] = []
	for (let i = 0; i < arr.length; i += chunkSize) {
		out.push(arr.slice(i, i + chunkSize))
	}
	return out
}

/** Maps persisted submission values back to fieldKey → display string. */
export function parseSubmissionValuesToFieldMap(
	formDetail: FormDefinitionDetail,
	values: FormSubmissionValueRequest[],
): Record<string, string> {
	const fieldKeyById = new Map(formDetail.fields.map((f) => [f.id, f.fieldKey]))
	const out: Record<string, string> = {}
	for (const v of values ?? []) {
		const fieldKey = fieldKeyById.get(v.formFieldId)
		if (!fieldKey) continue
		const raw =
			v.dropdownValue ??
			v.textValue ??
			(v.numberValue !== null && v.numberValue !== undefined ? String(v.numberValue) : null) ??
			(v.currencyValue !== null && v.currencyValue !== undefined
				? String(v.currencyValue)
				: null) ??
			v.dateValue ??
			v.timeValue ??
			''
		out[fieldKey] = raw ?? ''
	}
	return out
}

/** Visible required fields with empty (trimmed) values. */
export function getMissingRequiredDynamicFormFields(
	formDetail: FormDefinitionDetail,
	fieldValues: Record<string, string>,
	isRequired: (f: FormField) => boolean = (f) => f.isRequired,
): FormField[] {
	return formDetail.fields.filter(
		(f) => !f.isHidden && isRequired(f) && (fieldValues[f.fieldKey] ?? '').trim() === '',
	)
}

/**
 * Missing required Work Scope Registry fields. Purchase Order is always required and Work Order
 * only when the chosen purchase order needs one, whatever the form fields are set to. Pass the
 * saved values when editing so a saved purchase order and work order are kept as they are.
 */
export function getMissingWorkScopeRegistryFields(
	formDetail: FormDefinitionDetail,
	fieldValues: Record<string, string>,
	billingLogs: BillingLogEntryResponse[],
	savedValues?: BillingOrderValues,
): FormField[] {
	return getMissingRequiredDynamicFormFields(formDetail, fieldValues, (f) => {
		if (f.fieldKey === 'purchaseOrder') return true
		if (f.fieldKey === 'workOrder') return formNeedsWorkOrder(billingLogs, fieldValues, savedValues)
		return f.isRequired
	})
}

/** Per-fieldKey error messages for the given missing fields. */
export function requiredFieldErrorsFor(missingFields: FormField[]): Record<string, string> {
	const out: Record<string, string> = {}
	for (const f of missingFields) {
		out[f.fieldKey] = `${f.label} is required`
	}
	return out
}

/** Per-fieldKey error messages for missing required values (UI validation). */
export function buildRequiredFieldErrors(
	formDetail: FormDefinitionDetail,
	fieldValues: Record<string, string>,
): Record<string, string> {
	return requiredFieldErrorsFor(getMissingRequiredDynamicFormFields(formDetail, fieldValues))
}

/**
 * Maps dynamic form field UI state (string keyed by fieldKey) to FormDefinitions submission values.
 * Skips blank fields; throws if number/currency parsing fails.
 */
export function buildSubmissionValues(
	formDetail: FormDefinitionDetail,
	fieldValues: Record<string, string>,
): FormSubmissionRequest['values'] {
	const visibleFields = formDetail.fields.filter((f) => !f.isHidden)
	const out: FormSubmissionRequest['values'] = []

	for (const f of visibleFields) {
		const raw = (fieldValues[f.fieldKey] ?? '').trim()
		if (!raw) continue

		switch (f.fieldType) {
			case 'text':
				out.push({ formFieldId: f.id, textValue: raw })
				break
			case 'dropdown':
				out.push({ formFieldId: f.id, dropdownValue: raw })
				break
			case 'number': {
				const n = Number(raw)
				if (Number.isNaN(n)) throw new Error(`Invalid number for "${f.label}".`)
				out.push({ formFieldId: f.id, numberValue: n })
				break
			}
			case 'currency': {
				const n = Number(raw)
				if (Number.isNaN(n)) throw new Error(`Invalid currency for "${f.label}".`)
				out.push({ formFieldId: f.id, currencyValue: n })
				break
			}
			case 'date':
				out.push({ formFieldId: f.id, dateValue: raw })
				break
			case 'time':
				out.push({ formFieldId: f.id, timeValue: raw })
				break
			default:
				out.push({ formFieldId: f.id, textValue: raw })
				break
		}
	}

	return out
}
